/**
 * Setup District Boundaries
 * Initializes district boundary tables and data in the database
 */
import { supabase } from "@/integrations/supabase/client";
import { NWSISD_MEMBER_DISTRICTS } from "./district/districtBoundaries";
import { EnhancedErrorLogger } from '@/utils/error-tracking/enhanced-logger';

/**
 * Sets up the district_boundaries table if it doesn't exist
 */
export async function setupDistrictBoundariesTable(): Promise<void> {
  try {
    
    // Check if district_boundaries table exists
    const { data, error } = await supabase
      .from('district_boundaries')
      .select('district_name')
      .limit(1);
    
    if (error) {
      
      // Handle the case where table doesn't exist
      if (error.message && error.message.includes('relation "district_boundaries" does not exist')) {
        await createDistrictBoundariesTable();
      }
      
      return;
    }
    
    // If we got here, the table exists, check if it has data
    if (!data || data.length === 0) {
      await createDefaultDistrictRecords();
    } else {
    }
    
  } catch (error) {
    // Handle error with type casting
    const pgError = error as Error;
  }
}

/**
 * Creates the district_boundaries table using direct SQL
 */
async function createDistrictBoundariesTable(): Promise<void> {
  try {
    // Instead of using SQL stored procedure, we'll check if the district_boundaries table exists in the schema list
    // Note: This approach is for frontend development only
    // In production, proper migrations should be used
    
    
    // Since we can't directly execute SQL from the client,
    // we'll proceed assuming the table will be created through migrations
    
    // Then create default district records
    await createDefaultDistrictRecords();
    
  } catch (error) {
    // Handle error with type casting
    const pgError = error as Error;
  }
}

/**
 * Creates default district records in the database
 */
async function createDefaultDistrictRecords(): Promise<void> {
  try {
    // Check if records already exist to avoid duplicate inserts
    const { data: existingRecords, error: checkError } = await supabase
      .from('district_boundaries')
      .select('district_code')
      .limit(1);
    
    if (checkError) {
      return;
    }
    
    // If records already exist, skip creation
    if (existingRecords && existingRecords.length > 0) {
      return;
    }
    
    // Batch insert all district records at once for better performance
    const districtRecords = NWSISD_MEMBER_DISTRICTS.map(district => ({
      district_name: district.name,
      district_code: district.number.toString(),
      // Empty placeholder geometry until actual boundaries are imported
      boundary: {
        type: 'MultiPolygon',
        coordinates: []
      }
    }));
    
    const { error } = await supabase
      .from('district_boundaries')
      .insert(districtRecords);
    
    if (error) {
    }
    
  } catch (error) {
    // Handle error with type casting
    const pgError = error as Error;
  }
}

/**
 * Creates SQL functions for geospatial district lookups
 * Note: These functions are now created directly in the database through SQL migrations
 */
export async function setupGeospatialFunctions(): Promise<void> {
  try {

    // Test if find_school_district function exists by calling it with test coordinates
    try {
      const { data, error } = await supabase
        .rpc('find_school_district', { lat: 45.0, lon: -93.3 });
      
      if (!error) {
      } else {
      }
    } catch (fnError) {
      console.error('Setup district boundaries failed:', fnError);
      EnhancedErrorLogger.getInstance().logQuickError(fnError instanceof Error ? fnError : new Error(String(fnError)), 'SetupBoundaries', { context: 'setup district boundaries' });
    }

  } catch (error) {
    const pgError = error as Error;
  }
}
